import { useCallback, useEffect, useState } from "react";
import { useDependency } from "../../../../contexts/DependencyProvider";
import {
  Flex,
  View,
  TextField,
  MenuTrigger,
  Menu,
  Item,
  ActionButton,
  Text,
} from "@adobe/react-spectrum";
import Edit from "@spectrum-icons/workflow/Edit";
import {
  AtollonIcon,
  PrintIcon,
  EditIcon,
  ToolIcon,
  TrashIcon,
  MassmailGreen,
  MassmailRed,
  TwitterIcon,
  FacebookIcon,
  LinkdinIcon,
  ClientIconBlue,
} from "../../../../components/protected/Icons/IconsLib";
import { ToastQueue } from '@adobe/react-spectrum';
import { useTranslation } from "react-i18next";
import { ComboBox, Key } from "@adobe/react-spectrum";
import ButtonsComponent from "./ButtonsComponent";
import { GetContextPathRequest } from "../../../../services/soap/project/requests/GetContextPathRequest";
import { GetFolderRequest } from "../../../../services/soap/project/requests/GetFolderRequest";
import { UpdateFolderRequest } from "../../../../services/soap/project/requests/UpdateFolderRequest";
import { CheckWorkFlowQueueRequest } from "../../../../services/soap/features/requests/CheckWorkFlowQueueRequest";
import { FPAData, FPADataTypes } from "../../../../infra/protected/FPA/FPAData";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../../state/store";
import { setSelectedItem } from "../../../../state/Finder/finderSlice";
import styles from "./HeaderToolbarComponent.module.css";
import { usePreloadAssets } from "../../../../hooks/UsePreloadAssets";
import { getDisplayContextPathNodes } from "./contextPathUtils";
import { ListMessageContainerNotificationRequest } from "../../../../services/soap/message/requests/ListMessageContainerNotificationRequest";
import type { Filter } from "../../../../services/soap/dataObjects/Filter";
import { CreateMessageContainerNotificationRequest } from "../../../../services/soap/message/requests/CreateMessageContainerNotificationRequest";
import { DeleteMessageContainerNotificationRequest } from "../../../../services/soap/message/requests/DeleteMessageContainerNotificationRequest";
import { publish, AtollonEvent } from "../../../../infra/events/Events";
interface IFolderComponentProps {
  headerData: any;
  statusColor: (color: string) => string;
  onPrint?: (item: FPAData) => void;
  onEdit?: (item: FPAData) => void;
  onDelete?: (item: FPAData) => void;
  onTool?: (item: FPAData) => void;
  onAI?: (item: FPAData) => void;
  selectedItem?: FPAData;
}

const FolderComponent = ({
  headerData,
  statusColor,
  onPrint,
  onEdit,
  onDelete,
  onTool,
  onAI,
  selectedItem,
}: IFolderComponentProps) => {
  const { store, projectService, featureService, messageService } = useDependency();
  const { folderStatusMapping2 } = usePreloadAssets();
  const [folderStatusList, setFolderStatusList] = useState<any>([]);
  const [fullPath, setFullPath] = useState<string>("");
  const [folderData, setFolderData] = useState<any>([]);
  let [activityStatus, setActivityStatus] = useState("");
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();

  const [isTeamInbox, setIsTeamInbox] = useState(false);
  const [teamInboxRowId, setTeamInboxRowId] = useState<string | null>(null);

  // Editing states
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingRefId, setIsEditingRefId] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [editedRefId, setEditedRefId] = useState("");

  // Local state to track updated values
  const [displayName, setDisplayName] = useState<string>("");
  const [displayRefId, setDisplayRefId] = useState<string>("");

  // State to control menu open/close for double-click support
  const [isNameMenuOpen, setIsNameMenuOpen] = useState(false);
  const [isRefIdMenuOpen, setIsRefIdMenuOpen] = useState(false);

  // Update display values when headerData changes
  useEffect(() => {
    setDisplayName(headerData?.name || "");
    setDisplayRefId(headerData?.referenceId || "");
  }, [headerData?.name, headerData?.referenceId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsTeamInbox(false);
      setTeamInboxRowId(null);
      const userId = String(store.UserId || "").trim();
      const containerId = String(headerData?.messageDefault ?? "").trim();
      if (!userId || !containerId) {
        return;
      }
      try {
        const FILTER: Filter = {
          COL: [{ name: "MessageRecipient", type: "equals", value: userId }],
        };
        const res: any = await messageService.listMessageContainerNotification(
          new ListMessageContainerNotificationRequest(store.Server, store.SessionId, {
            limit: "200",
            offset: "0",
            FILTER,
          }),
        );
        const rows = Array.isArray(res?.ROW) ? res.ROW : [];
        const matched = rows.find((r: any) => {
          const name = String(r?.MessageContainerName ?? "");
          if (name === "My Messages") return false;
          const container = String(r?.MessageContainer ?? "").trim();
          return container === containerId;
        });
        if (!cancelled && matched?.id) {
          setIsTeamInbox(true);
          setTeamInboxRowId(String(matched.id));
        }
      } catch (e) {
        console.error("Team inbox status lookup failed", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [headerData?.messageDefault, messageService, store.Server, store.SessionId, store.UserId]);

  const handleTeamInboxToggle = useCallback(async () => {
    const userId = String(store.UserId || "").trim();
    const msgDefault = String(headerData?.messageDefault ?? "").trim();
    const msgGroup = String(headerData?.messageGroup ?? "").trim();
    const msgPath = String(headerData?.messageDefaultPath ?? "").trim();
    const name = String(headerData?.name ?? selectedItem?.title ?? "").trim();
    if (!userId || !msgDefault || !msgGroup) {
      ToastQueue.negative(
        t("team_inbox_missing_container", {
          ns: "layout_components",
          defaultValue:
            "This folder cannot be configured as Team Inbox (missing message container data).",
        }),
        { timeout: 2500 },
      );
      return;
    }

    try {
      if (!isTeamInbox) {
        const res: any = await messageService.createMessageContainerNotification(
          new CreateMessageContainerNotificationRequest(store.Server, store.SessionId, {
            MessageRecipient: userId,
            MessageContainerName: name,
            MessageContainerPath: msgPath,
            MessageContainerGroup: msgGroup,
            MessageContainer: msgDefault,
          }),
        );
        if (res?.result !== "OK") {
          ToastQueue.negative(res?.EXCEPTION?.message || "Failed to set Team Inbox", {
            timeout: 2500,
          });
          return;
        }
        setIsTeamInbox(true);
        publish(AtollonEvent.TEAM_INBOX_UPDATED);
        ToastQueue.positive(
          t("team_inbox_set", {
            ns: "layout_components",
            defaultValue: "Team Inbox configured",
          }),
          { timeout: 1500 },
        );
        // Resolve row id for remove
        try {
          const FILTER: Filter = {
            COL: [{ name: "MessageRecipient", type: "equals", value: userId }],
          };
          const listRes: any = await messageService.listMessageContainerNotification(
            new ListMessageContainerNotificationRequest(store.Server, store.SessionId, {
              limit: "200",
              offset: "0",
              FILTER,
            }),
          );
          const rows = Array.isArray(listRes?.ROW) ? listRes.ROW : [];
          const matched = rows.find((r: any) => String(r?.MessageContainer ?? "").trim() === msgDefault);
          if (matched?.id) {
            setTeamInboxRowId(String(matched.id));
          }
        } catch {
          // ignore
        }
        return;
      }

      const id = String(teamInboxRowId ?? "").trim();
      if (!id) {
        ToastQueue.negative("Missing Team Inbox id", { timeout: 1500 });
        return;
      }
      const res: any = await messageService.deleteMessageContainerNotification(
        new DeleteMessageContainerNotificationRequest(store.Server, store.SessionId, id),
      );
      if (res?.result !== "OK") {
        ToastQueue.negative(res?.EXCEPTION?.message || "Failed to remove Team Inbox", {
          timeout: 2500,
        });
        return;
      }
      setIsTeamInbox(false);
      setTeamInboxRowId(null);
      publish(AtollonEvent.TEAM_INBOX_UPDATED);
      ToastQueue.positive(
        t("team_inbox_removed", {
          ns: "layout_components",
          defaultValue: "Team Inbox removed",
        }),
        { timeout: 1500 },
      );
    } catch (e: any) {
      ToastQueue.negative(e?.message || "Team Inbox update failed", { timeout: 2500 });
    }
  }, [
    headerData?.messageDefault,
    headerData?.messageDefaultPath,
    headerData?.messageGroup,
    headerData?.name,
    isTeamInbox,
    messageService,
    selectedItem?.title,
    store.Server,
    store.SessionId,
    store.UserId,
    t,
    teamInboxRowId,
  ]);

  const getFolderStatusArray = useCallback(async () => {
    if (folderStatusMapping2) {
      const statusArray: any = Object.keys(folderStatusMapping2).map(
        (key: string) => ({
          id: key,
          name: (folderStatusMapping2 as any)[key].name,
          color: (folderStatusMapping2 as any)[key].color,
          status: (folderStatusMapping2 as any)[key].status,
        }),
      );
      var filtered_status = statusArray.filter(
        (item: any) =>
          item.status.TYPE &&
          item.status.TYPE.find(
            (type: any) => type.id === headerData?.folderType,
          ),
      );
      setFolderStatusList(filtered_status);
    }
  }, [folderStatusMapping2, headerData?.folderType]);

  useEffect(() => {
    (async () => {
      if (
        headerData &&
        headerData.id &&
        folderStatusMapping2 &&
        getFolderStatusArray
      ) {
        headerData && (await getFullPath());
        await getFolderData();
        getFolderStatusArray();
        setActivityStatus(headerData?.customState);
      }
    })();
  }, [headerData?.id, folderStatusMapping2, getFolderStatusArray]);

  const getFullPath = async () => {
    const response = await projectService.getContextPath(
      new GetContextPathRequest(
        store.Server,
        store.SessionId,
        headerData?.id || 0,
      ),
    );
    const pathString = getDisplayContextPathNodes(
      response.LIST,
      headerData?.id,
      true,
    )
      .map((item) => item.name)
      .join(" > ");
    setFullPath(pathString);
  };

  async function getFolderData() {
    if (selectedItem) {
      let { FOLDER } = await projectService.getFolder(
        new GetFolderRequest(store.Server, store.SessionId, selectedItem.id),
      );
      setFolderData(FOLDER);
    }
  }

  async function updateFolderStatus(key: Key | null) {
    if (key === null) {
      // Handle clear action
      let resFolder = await projectService.updateFolder(
        new UpdateFolderRequest(store.Server, store.SessionId, {
          ...folderData,
          customState: "",
        }),
      );
      if (resFolder.result === "OK") {
        await featureService.checkWorkFlowQueue(
          new CheckWorkFlowQueueRequest(store.Server, store.SessionId),
        );
        ToastQueue.positive(
          t("status_updated_successfully", { ns: "layout_components" }),
        );
        setActivityStatus("");
        dispatch(
          setSelectedItem({
            item: selectedItem
              ? { ...selectedItem, statusName: "", statusColor: "" }
              : null,
            show_context: false,
          }),
        );
      } else {
        ToastQueue.negative(resFolder.EXCEPTION?.message, { timeout: 1000 });
      }
      return;
    }

    const value = key.toString();
    let resFolder = await projectService.updateFolder(
      new UpdateFolderRequest(store.Server, store.SessionId, {
        ...folderData,
        comment: selectedItem?.comment ?? folderData?.comment,
        customState: value,
      }),
    );
    let status = folderStatusList.find((status: any) => status.id === value);
    if (resFolder.result === "OK") {
      await featureService.checkWorkFlowQueue(
        new CheckWorkFlowQueueRequest(store.Server, store.SessionId),
      );
      ToastQueue.positive(
        t("status_updated_successfully", { ns: "layout_components" }),
      );
      setActivityStatus(value);
      dispatch(
        setSelectedItem({
          item: selectedItem
            ? {
                ...selectedItem,
                statusName: status.name,
                statusColor: statusColor(status.color),
              }
            : null,
          show_context: false,
        }),
      );
    } else {
      ToastQueue.negative(resFolder.EXCEPTION?.message, { timeout: 1000 });
    }
  }

  const getAddress = () => {
    return headerData?.CONTACT?.COMPANY?.street !== undefined &&
      headerData?.CONTACT?.COMPANY?.street !== null
      ? headerData?.CONTACT?.COMPANY?.street +
          " " +
          headerData?.CONTACT?.COMPANY?.city +
          " " +
          headerData?.CONTACT?.COMPANY?.zip +
          " " +
          headerData?.CONTACT?.COMPANY?.country
      : "";
  };

  const copyUrl = () => {
    const url = `${window.location.origin}/finder?id=${selectedItem?.id}`;
    navigator.clipboard.writeText(url);
    ToastQueue.positive(
      t("link_copied_to_clipboard", { ns: "layout_components" }),
      { timeout: 1000 },
    );
  };

  // Handler to start editing folder name
  const handleEditName = () => {
    setEditedName(displayName || "");
    setIsEditingName(true);
    setIsNameMenuOpen(false);
  };

  // Handler to start editing referenceId
  const handleEditRefId = () => {
    setEditedRefId(displayRefId || "");
    setIsEditingRefId(true);
    setIsRefIdMenuOpen(false);
  };

  // Handler to save folder name
  const handleSaveName = async () => {
    if (!editedName.trim() || editedName === displayName) {
      setIsEditingName(false);
      return;
    }

    try {
      let resFolder = await projectService.updateFolder(
        new UpdateFolderRequest(store.Server, store.SessionId, {
          ...folderData,
          name: editedName,
        }),
      );

      if (resFolder.result === "OK") {
        await featureService.checkWorkFlowQueue(
          new CheckWorkFlowQueueRequest(store.Server, store.SessionId),
        );
        ToastQueue.positive(
          t("folder_name_updated_successfully", {
            ns: "layout_components",
            defaultValue: "Folder name updated successfully",
          }),
          {
            timeout: 1000,
          },
        );
        // Update local display state
        setDisplayName(editedName);
        dispatch(
          setSelectedItem({
            item: selectedItem
              ? {
                  ...selectedItem,
                  title: editedName,
                }
              : null,
            show_context: false,
          }),
        );
        // Refresh folder data
        await getFolderData();
      } else {
        ToastQueue.negative(resFolder.EXCEPTION?.message, { timeout: 1000 });
      }
    } catch (error: any) {
      ToastQueue.negative(
        error?.message ||
          t("failed_to_update_folder", {
            ns: "layout_components",
            defaultValue: "Failed to update folder",
          }),
        {
        timeout: 1000,
        },
      );
    }
    setIsEditingName(false);
  };

  // Handler to save referenceId
  const handleSaveRefId = async () => {
    if (editedRefId === displayRefId) {
      setIsEditingRefId(false);
      return;
    }

    try {
      let resFolder = await projectService.updateFolder(
        new UpdateFolderRequest(store.Server, store.SessionId, {
          ...folderData,
          referenceId: editedRefId,
        }),
      );

      if (resFolder.result === "OK") {
        await featureService.checkWorkFlowQueue(
          new CheckWorkFlowQueueRequest(store.Server, store.SessionId),
        );
        ToastQueue.positive(
          t("refid_updated_successfully", {
            ns: "layout_components",
            defaultValue: "RefId updated successfully",
          }),
          {
          timeout: 1000,
          },
        );
        // Update local display state
        setDisplayRefId(editedRefId);
        // Refresh folder data
        await getFolderData();
      } else {
        ToastQueue.negative(resFolder.EXCEPTION?.message, { timeout: 1000 });
      }
    } catch (error: any) {
      ToastQueue.negative(
        error?.message ||
          t("failed_to_update_folder", {
            ns: "layout_components",
            defaultValue: "Failed to update folder",
          }),
        {
        timeout: 1000,
        },
      );
    }
    setIsEditingRefId(false);
  };

  // Handler to cancel editing
  const handleCancelEdit = (type: "name" | "refId") => {
    if (type === "name") {
      setIsEditingName(false);
      setEditedName("");
    } else {
      setIsEditingRefId(false);
      setEditedRefId("");
    }
  };

  return (
    <Flex width="100%">
      <View UNSAFE_className={styles.header_main}>
        <View UNSAFE_className={styles.header_top_row}>
        <View UNSAFE_className={styles.header_left_column}>
        <View UNSAFE_className={styles.header_child}>
          <View UNSAFE_className={styles.frame_parent}>
            <View UNSAFE_className={styles.icon_activity_parent}>
              <ClientIconBlue size={32} />
              {isEditingName ? (
                <TextField
                  value={editedName}
                  onChange={setEditedName}
                  onBlur={handleSaveName}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSaveName();
                    if (e.key === "Escape") handleCancelEdit("name");
                  }}
                  width="250px"
                  autoFocus
                  isQuiet
                />
              ) : (
                <MenuTrigger
                  trigger="longPress"
                  isOpen={isNameMenuOpen}
                  onOpenChange={setIsNameMenuOpen}
                >
                  <div onDoubleClick={() => setIsNameMenuOpen(true)}>
                    <ActionButton
                      isQuiet
                      UNSAFE_style={{
                        padding: 0,
                        minWidth: "auto",
                        background: "transparent",
                        border: "none",
                        cursor: "text",
                        height: "auto",
                        fontWeight: "bold",
                      }}
                      UNSAFE_className={styles.activity_name}
                    >
                      <span
                        style={{ fontSize: "20px", padding: "5px" }}
                        title={displayName}
                      >
                        {displayName?.length > 45
                          ? displayName.substring(0, 45) + "..."
                          : displayName}
                      </span>
                    </ActionButton>
                  </div>
                  <Menu onAction={(key) => key === "edit" && handleEditName()}>
                    <Item key="edit">
                      <Edit size="S" />
                      <Text>
                        {t("edit_name", {
                          ns: "layout_components",
                          defaultValue: "Edit Name",
                        })}
                      </Text>
                    </Item>
                  </Menu>
                </MenuTrigger>
              )}
            </View>
            <View UNSAFE_className={styles.folder_name_parent}>
              <View
                UNSAFE_className={styles.folder_name}
                UNSAFE_style={{ wordBreak: "break-all" }}
              >
                {fullPath.split(" > ").map((part, index, array) => (
                  <span key={index}>
                    {part}
                    {index < array.length - 1 && (
                      <span style={{ color: "#9CA3AF", margin: "0 4px" }}>
                        {" "}
                        &gt;{" "}
                      </span>
                    )}
                  </span>
                ))}
              </View>
            </View>
          </View>
          <Flex
            direction={"row"}
            justifyContent={"space-between"}
            UNSAFE_className={styles.tag_parent}
            marginTop={20}
          >
            <View
              UNSAFE_style={{
                display: "flex",
                flexDirection: "row",
                gap: "20px",
              }}
            >
              <View UNSAFE_className={styles.tag}>
                <View UNSAFE_className={styles.activity_type}>
                  <View UNSAFE_className={styles.activity_type_text}>
                    {headerData?.folderTypeName}
                  </View>

                  {isEditingRefId ? (
                    <>
                      <View UNSAFE_className={styles.divider}></View>
                      <TextField
                        value={editedRefId}
                        onChange={setEditedRefId}
                        onBlur={handleSaveRefId}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSaveRefId();
                          if (e.key === "Escape") handleCancelEdit("refId");
                        }}
                        width="150px"
                        autoFocus
                        isQuiet
                      />
                    </>
                  ) : displayRefId ? (
                    <>
                      <View
                        UNSAFE_className={styles.divider}
                        UNSAFE_style={{ backgroundColor: "#fff" }}
                      ></View>
                      <MenuTrigger
                        trigger="longPress"
                        isOpen={isRefIdMenuOpen}
                        onOpenChange={setIsRefIdMenuOpen}
                      >
                        <div onDoubleClick={() => setIsRefIdMenuOpen(true)}>
                          <ActionButton
                            isQuiet
                            UNSAFE_style={{
                              padding: 0,
                              minWidth: "auto",
                              background: "transparent",
                              border: "none",
                              cursor: "text",
                              height: "auto",
                            }}
                          >
                            <span
                              style={{ fontSize: "12px", padding: "5px" }}
                              className={styles.ref_id}
                            >
                              {displayRefId}
                            </span>
                          </ActionButton>
                        </div>
                        <Menu
                          onAction={(key) =>
                            key === "edit" && handleEditRefId()
                          }
                        >
                          <Item key="edit">
                            <Edit size="S" />
                            <Text>
                              {t("edit_refid", {
                                ns: "layout_components",
                                defaultValue: "Edit RefID",
                              })}
                            </Text>
                          </Item>
                        </Menu>
                      </MenuTrigger>
                    </>
                  ) : (
                    <>
                      <View
                        UNSAFE_className={styles.divider}
                        UNSAFE_style={{ backgroundColor: "#fff" }}
                      ></View>
                      <MenuTrigger
                        trigger="longPress"
                        isOpen={isRefIdMenuOpen}
                        onOpenChange={setIsRefIdMenuOpen}
                      >
                        <div onDoubleClick={() => setIsRefIdMenuOpen(true)}>
                          <ActionButton
                            isQuiet
                            UNSAFE_style={{
                              padding: 0,
                              minWidth: "auto",
                              background: "transparent",
                              border: "none",
                              cursor: "text",
                              height: "auto",
                            }}
                          >
                            <span
                              style={{ fontSize: "12px", padding: "5px" }}
                              className={styles.ref_id}
                            >
                              {t("add_refid", {
                                ns: "layout_components",
                                defaultValue: "Add RefID",
                              })}
                            </span>
                          </ActionButton>
                        </div>
                        <Menu
                          onAction={(key) =>
                            key === "edit" && handleEditRefId()
                          }
                        >
                          <Item key="edit">
                            <Edit size="S" />
                            <Text>
                              {t("add_refid", {
                                ns: "layout_components",
                                defaultValue: "Add RefID",
                              })}
                            </Text>
                          </Item>
                        </Menu>
                      </MenuTrigger>
                    </>
                  )}
                </View>
              </View>

              <View UNSAFE_className={styles.io_parent}>
                {headerData?.CONTACT?.COMPANY?.COMPANYINFO.regNumber !==
                undefined ? (
                  <>
                    <View UNSAFE_className={styles.io_label}>IČO</View>
                    <View UNSAFE_className={styles.io_text}>
                      {headerData?.CONTACT?.COMPANY?.COMPANYINFO.regNumber}
                    </View>
                  </>
                ) : (
                  ""
                )}
              </View>

              <View UNSAFE_className={styles.di_parent}>
                {headerData?.CONTACT?.COMPANY?.COMPANYINFO.VATNumber !==
                undefined ? (
                  <>
                    <View UNSAFE_className={styles.di_label}>DIČ</View>
                    <View UNSAFE_className={styles.di_text}>
                      {headerData?.CONTACT?.COMPANY?.COMPANYINFO.VATNumber}
                    </View>
                  </>
                ) : (
                  ""
                )}
              </View>

              <View UNSAFE_className={styles.address_parent}>
                {getAddress() !== "" ? (
                  <>
                    <View UNSAFE_className={styles.address_child}>
                      {t("address", { ns: "layout_components" })}
                    </View>
                    <View UNSAFE_className={styles.address}>
                      {getAddress()}
                    </View>
                  </>
                ) : (
                  ""
                )}
              </View>
            </View>

          </Flex>
        </View>
        </View>
        <View UNSAFE_className={styles.header_right_column}>
        <View UNSAFE_className={styles.header_actions_row}>
          {headerData?.CONTACT?.COMPANY?.adsAllowed === "N" ? (
            <View UNSAFE_className={styles.ad_not_allowed}>
              <MassmailRed size={20} />
            </View>
          ) : (
            <View UNSAFE_className={styles.ad_allowed}>
              <MassmailGreen size={20} />
            </View>
          )}

          <View UNSAFE_className={styles.status_picker_wrap}>
            <ComboBox
              width={"100%"}
              selectedKey={activityStatus || null}
              onSelectionChange={updateFolderStatus}
              allowsCustomValue={false}
              aria-label={t("status", { ns: "layout_components" })}
            >
              {folderStatusList.map((item: any) => (
                <Item key={item.id} textValue={item.name}>
                  <View
                    UNSAFE_style={{
                      height: 40,
                      width: 200,
                      backgroundColor: statusColor(item.color),
                      padding: 8,
                    }}
                  >
                    {item.name}
                  </View>
                </Item>
              ))}
            </ComboBox>
          </View>

          <View UNSAFE_className={styles.actions_wrap}>
            <ButtonsComponent
              styles={styles}
              AtollonIcon={AtollonIcon}
              PrintIcon={PrintIcon}
              EditIcon={EditIcon}
              ToolIcon={ToolIcon}
              TrashIcon={TrashIcon}
              onPrint={onPrint}
              onEdit={onEdit}
              onDelete={onDelete}
              onTool={onTool}
              onAI={onAI}
              selectedItem={selectedItem}
              onCopyLink={copyUrl}
              extraMenuItems={[
                selectedItem?.type === FPADataTypes.FOLDER
                  ? {
                      key: "teamInboxToggle",
                      name: isTeamInbox
                        ? t("remove_team_inbox", {
                            ns: "layout_components",
                            defaultValue: "Remove Team Inbox",
                          })
                        : t("mark_as_team_inbox", {
                            ns: "layout_components",
                            defaultValue: "Mark as Team Inbox",
                          }),
                      onAction: () => {
                        void handleTeamInboxToggle();
                      },
                    }
                  : null,
              ].filter(Boolean) as Array<{ key: string; name: string; onAction: () => void }>}
            />
          </View>
        </View>
        <View UNSAFE_className={styles.socials_icons}>
          <View UNSAFE_className={styles.icon_twitter}>
            <TwitterIcon size={24} />
          </View>

          <View UNSAFE_className={styles.icon_facebook}>
            <FacebookIcon size={24} />
          </View>

          <View UNSAFE_className={styles.icon_linkedin}>
            <LinkdinIcon size={24} />
          </View>
        </View>
        </View>
        </View>
      </View>
    </Flex>
  );
};

export default FolderComponent;
